/**
 * Offline mutation outbox — durable writes when connectivity is unavailable.
 *
 * Stores TanStack DB write-through mutations in localStorage and replays them
 * when the browser comes back online. Background sync is requested so the
 * installed PWA can flush the queue as soon as connectivity returns.
 */

import { logger } from "@/lib/logger";

const OFFLINE_STORE_KEY = "flowstate-automate-offline-mutations-v1";

export type MutationOperation = "insert" | "update" | "delete";

export interface QueuedMutation {
  id: string;
  tableName: string;
  operation: MutationOperation;
  label: string;
  mutations: Array<{
    key: string;
    modified: Record<string, unknown>;
    original?: Record<string, unknown>;
  }>;
  enqueuedAt: string;
}

export function isOnline(): boolean {
  return typeof navigator !== "undefined" ? navigator.onLine : true;
}

function isValidMutationOperation(value: unknown): value is MutationOperation {
  return value === "insert" || value === "update" || value === "delete";
}

function isValidQueuedMutation(item: unknown): item is QueuedMutation {
  if (typeof item !== "object" || item === null) return false;
  const m = item as Record<string, unknown>;
  if (
    typeof m.id !== "string" ||
    typeof m.tableName !== "string" ||
    !isValidMutationOperation(m.operation) ||
    !Array.isArray(m.mutations) ||
    typeof m.enqueuedAt !== "string"
  ) {
    return false;
  }
  return m.mutations.every((mutation) => {
    if (typeof mutation !== "object" || mutation === null) return false;
    const row = mutation as Record<string, unknown>;
    return (
      typeof row.key === "string" &&
      typeof row.modified === "object" &&
      row.modified !== null
    );
  });
}

function readStore(): QueuedMutation[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(OFFLINE_STORE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    const list = Array.isArray(parsed) ? parsed : [];
    const valid = list.filter(isValidQueuedMutation);
    if (valid.length !== list.length) {
      logger.warn("[offline-queue] dropped invalid offline queue entries", {
        total: list.length,
        valid: valid.length,
      });
      writeStore(valid);
    }
    return valid;
  } catch (err) {
    logger.warn("[offline-queue] failed to read store", { error: err });
    return [];
  }
}

function writeStore(queue: QueuedMutation[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(OFFLINE_STORE_KEY, JSON.stringify(queue));
  } catch (err) {
    logger.warn("[offline-queue] failed to write store", { error: err });
  }
}

export function getOfflineQueue(): QueuedMutation[] {
  return readStore();
}

export function getPendingMutationCount(): number {
  return readStore().length;
}

export function enqueueOfflineMutation(mutation: QueuedMutation): void {
  if (!isValidQueuedMutation(mutation)) {
    logger.error("[offline-queue] invalid mutation rejected", { mutation });
    return;
  }
  const queue = readStore();
  if (queue.some((m) => m.id === mutation.id)) {
    logger.debug("[offline-queue] duplicate mutation ignored", { id: mutation.id });
    return;
  }
  queue.push(mutation);
  writeStore(queue);
  logger.info("[offline-queue] mutation enqueued", {
    id: mutation.id,
    operation: mutation.operation,
    tableName: mutation.tableName,
  });
}

export function removeOfflineMutation(id: string): void {
  const queue = readStore().filter((m) => m.id !== id);
  writeStore(queue);
}

export function clearOfflineQueue(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(OFFLINE_STORE_KEY);
  } catch {
    // ignore
  }
}

export async function requestBackgroundSync(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sync = (registration as unknown as { sync?: { register: (tag: string) => Promise<void> } }).sync;
    if (sync) {
      await sync.register("flush-offline-queue");
    }
  } catch {
    // Background sync not supported or registration failed — online event will retry.
  }
}
